import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownToLine, Send, Package, Gift, TrendingUp, Users } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../stores/authStore';
import { Card } from '../components/ui/Card';
import { StatCard } from '../components/ui/StatCard';
import { Badge } from '../components/ui/Badge';
import { Logo } from '../components/ui/Logo';
import { Chest } from '../components/ui/Chest';
import { chestForVRank, CHEST_NAMES } from '../lib/chest';
import { fmt } from '../lib/format';

interface Wallet { usdtBalance: string; cpBalance: string; rpBalance: string }
interface BonusSummary {
  today: { dynamic: string; leadership: string; sameRank: string; total: string };
  allTime: { dynamic: string; leadership: string; sameRank: string; total: string };
}
interface Pkg { id: string; status: string }

export function HomePage() {
  const user = useAuth((s) => s.user);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [bonus, setBonus] = useState<BonusSummary | null>(null);
  const [hasActive, setHasActive] = useState(false);

  useEffect(() => {
    Promise.all([
      api<Wallet>('/wallet').then(setWallet),
      api<BonusSummary>('/wallet/bonus/summary').then(setBonus),
      api<{ packages: Pkg[] }>('/wallet/packages').then((r) =>
        setHasActive(r.packages.some((p) => p.status === 'ACTIVE')),
      ),
    ]).catch(() => { /* silent */ });
  }, []);

  const chestTier = chestForVRank(user?.vRank ?? 'V0');
  const chestState: 'closed' | 'open' = hasActive ? 'open' : 'closed';

  return (
    <div className="px-4 pt-4 space-y-4">
      {/* Brand bar */}
      <div className="flex items-center justify-between">
        <Logo size={36} />
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-wider text-fg-muted leading-none">Your Treasure</div>
            <div className="text-[11px] font-bold leading-tight">{CHEST_NAMES[chestTier]}</div>
          </div>
          <Chest tier={chestTier} state="closed" size={36} />
        </div>
      </div>

      {/* Hero banner — chest + welcome */}
      <Card glow className="!p-0 overflow-hidden relative">
        <div className="flex items-center">
          <div className="flex-1 p-5">
            <div className="text-xs font-semibold tracking-widest text-fg-muted uppercase">Welcome back</div>
            <div className="mt-1 text-lg font-bold">{user?.username}</div>
            <div className="mt-1 flex items-center gap-2">
              <Badge tone="accent">{user?.vRank}</Badge>
              <span className="text-xs text-fg-muted">
                Ref · <span className="text-accent font-mono">{user?.referralCode}</span>
              </span>
            </div>
            <Link
              to="/package"
              className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-accent hover:text-accent-hover"
            >
              Buy Package <ArrowDownToLine size={14} className="rotate-[-90deg]" />
            </Link>
          </div>
          <div className="relative w-32 h-32 mr-3 my-2 flex items-center justify-center">
            <Chest tier={chestTier} state={chestState} size={120} />
            {!hasActive && (
              <div className="absolute bottom-0 left-0 right-0 text-center text-[9px] text-fg-muted">
                Activate to open
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Balance hero */}
      <Card>
        <div className="text-[10px] font-semibold tracking-wider uppercase text-fg-muted">USDT Balance</div>
        <div className="mt-1 text-4xl font-extrabold font-mono text-accent">
          {wallet ? fmt(wallet.usdtBalance) : '—'}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <StatCard label="CP" value={fmt(wallet?.cpBalance ?? 0)} accent="cp" />
          <StatCard label="RP" value={fmt(wallet?.rpBalance ?? 0)} accent="rp" />
        </div>
      </Card>

      {/* Quick actions */}
      <Card>
        <div className="text-sm font-bold mb-3">Quick Actions</div>
        <div className="grid grid-cols-4 gap-3">
          <QuickAction to="/wallet?action=deposit" icon={<ArrowDownToLine size={20} />} label="Deposit" />
          <QuickAction to="/wallet?action=transfer" icon={<Send size={20} />} label="Transfer" />
          <QuickAction to="/package" icon={<Package size={20} />} label="Packages" />
          <QuickAction to="/hierarchy" icon={<Users size={20} />} label="Team" />
        </div>
      </Card>

      {/* Bonus summary */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold">Bonus Summary</div>
          <Link to="/bonus" className="text-xs text-accent font-semibold">View all →</Link>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-surface-2 border border-border rounded-xl p-3.5">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold tracking-wider uppercase text-fg-muted">
              <TrendingUp size={11} /> Today
            </div>
            <div className="mt-1 text-xl font-bold font-mono text-accent">
              {bonus ? fmt(bonus.today.total) : '—'}
            </div>
            <div className="text-[10px] text-fg-muted mt-0.5">USDT</div>
          </div>
          <div className="bg-surface-2 border border-border rounded-xl p-3.5">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold tracking-wider uppercase text-fg-muted">
              <Gift size={11} /> All Time
            </div>
            <div className="mt-1 text-xl font-bold font-mono">
              {bonus ? fmt(bonus.allTime.total) : '—'}
            </div>
            <div className="text-[10px] text-fg-muted mt-0.5">USDT</div>
          </div>
        </div>
      </Card>
    </div>
  );
}

function QuickAction({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <Link
      to={to}
      className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-surface-2 border border-border hover:border-accent transition-colors"
    >
      <span className="text-accent">{icon}</span>
      <span className="text-[11px] font-semibold text-fg">{label}</span>
    </Link>
  );
}
